import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import MultiSelect from './components/MultiSelect';
import PromoBox from './components/PromoBox';
import GroupedPromoBox from './components/GroupedPromoBox';
import PromoModal from './components/PromoModal';
import AdminPanel from './components/AdminPanel';
import FeedbackButton from './components/FeedbackButton';
import FeedbackModal from './components/FeedbackModal';
import { loginRequest, authFetch } from './authConfig';
import { assignPromoColors, initializePromoColors } from './utils/colors';
import { formatWeekRange, groupPromosByYearAndWeek, getWeekNumber } from './utils/dateUtils';

// Check if a year has ISO week 53
function yearHasWeek53(year) {
    // Check December 31st of the year - if it's in week 53 of that year, the year has 53 weeks
    const dec31 = new Date(year, 11, 31);
    const weekNum = getWeekNumber(dec31);
    // Dec 31 is in week 53 of this year if weekNum is 53
    // It could also be week 1 of next year, in which case we check Dec 28 (always in last week of its year)
    if (weekNum === 53) return true;
    const dec28 = new Date(year, 11, 28);
    return getWeekNumber(dec28) === 53;
}

// Helper function to group promos by name within a week
function groupPromosByName(promos) {
    const grouped = {};

    promos.forEach(promo => {
        const name = promo.name;
        if (!grouped[name]) {
            grouped[name] = {
                name: name,
                instances: [],
                // Use the color from the first instance
                color: promo.color,
                bgColor: promo.bgColor
            };
        }
        grouped[name].instances.push(promo);
    });

    // Sort instances within each group by start date
    Object.values(grouped).forEach(group => {
        group.instances.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    });

    // Return as array, sorted by the earliest start date of each group
    return Object.values(grouped).sort((a, b) =>
        new Date(a.instances[0].startDate) - new Date(b.instances[0].startDate)
    );
}

const API_BASE = process.env.REACT_APP_API_URL || '';

function AppContent() {
    const { instance } = useMsal();

    // User authentication state
    const [currentUser, setCurrentUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    // Data state
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [series, setSeries] = useState([]);
    const [items, setItems] = useState([]);
    const [promoTypes, setPromoTypes] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter state (pending - not yet applied)
    const [pendingCategories, setPendingCategories] = useState([]);
    const [pendingSubcategories, setPendingSubcategories] = useState([]);
    const [pendingSeries, setPendingSeries] = useState([]);
    const [pendingItems, setPendingItems] = useState([]);
    const [pendingPromoTypes, setPendingPromoTypes] = useState([]);
    const [pendingMonths, setPendingMonths] = useState([]);
    const [pendingSku, setPendingSku] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [promoNameSearch, setPromoNameSearch] = useState('');

    // Month options for the Promo Month filter
    const monthOptions = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Applied filter state
    const [appliedFilters, setAppliedFilters] = useState({
        categories: [],
        subcategories: [],
        series: [],
        items: [],
        promoTypes: [],
        months: [],
        promoName: '',
        sku: ''
    });

    // UI state
    const [modalPromo, setModalPromo] = useState(null);
    const [viewFilteredOnly, setViewFilteredOnly] = useState(false);
    const [viewFullCalendar, setViewFullCalendar] = useState(false);
    const [allPromotions, setAllPromotions] = useState(null);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);

    // Theme state
    const [theme, setTheme] = useState(() => {
        return localStorage.getItem('theme') || 'system';
    });

    // Apply theme to document root
    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'system') {
            root.removeAttribute('data-theme');
        } else {
            root.setAttribute('data-theme', theme);
        }
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Compute display icon for theme toggle
    const themeIcon = (() => {
        if (theme === 'dark') return '\u263E'; // moon
        if (theme === 'light') return '\u2600'; // sun
        // system - check actual preference
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) return '\u263E';
        return '\u2600';
    })();

    const cycleTheme = () => {
        // system -> dark -> light -> system
        if (theme === 'system') setTheme('dark');
        else if (theme === 'dark') setTheme('light');
        else setTheme('system');
    };

    // Check if current user is admin or superadmin
    const isSuperAdmin = currentUser?.role === 'superadmin';
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

    // Fetch current user on mount — MSAL guarantees we have a signed-in AAD identity by now.
    // /api/users/me checks the app_users table to confirm DB authorization.
    useEffect(() => {
        async function fetchCurrentUser() {
            try {
                const res = await authFetch(`${API_BASE}/api/users/me`);
                if (!res) return; // redirect in progress
                if (res.status === 401 || res.status === 403) {
                    const data = await res.json().catch(() => ({}));
                    setAuthError(data.error || 'Access denied');
                    setAuthLoading(false);
                    return;
                }
                if (!res.ok) {
                    let errorMsg = 'Failed to authenticate';
                    try {
                        const data = await res.json();
                        errorMsg = data.error || errorMsg;
                    } catch (e) {
                        // ignore JSON parse error
                    }
                    throw new Error(errorMsg);
                }
                const user = await res.json();
                setCurrentUser(user);
                setAuthLoading(false);
            } catch (err) {
                console.error('Auth error:', err);
                setAuthError(err.message || 'Authentication failed. Please try again.');
                setAuthLoading(false);
            }
        }
        fetchCurrentUser();
    }, []);

    // Fetch initial data (promo types and years only - categories will be fetched separately)
    useEffect(() => {
        async function fetchInitialData() {
            try {
                const [typesRes, yearsRes] = await Promise.all([
                    authFetch(`${API_BASE}/api/promo-types`),
                    authFetch(`${API_BASE}/api/years`)
                ]);

                setPromoTypes(await typesRes.json());
                // Get years and ensure 2026 is included, then reverse to show newest first
                let yearsData = await yearsRes.json();
                if (!yearsData.includes(2026)) {
                    yearsData.push(2026);
                }
                setYears(yearsData.sort((a, b) => b - a));
                setLoading(false);
            } catch (error) {
                console.error('Error fetching initial data:', error);
                setLoading(false);
            }
        }

        fetchInitialData();
    }, []);

    // Fetch categories (filtered by selected subcategories and series - bidirectional cascading)
    useEffect(() => {
        async function fetchCategories() {
            try {
                const params = new URLSearchParams();
                if (pendingSubcategories.length > 0) {
                    params.set('subcategories', pendingSubcategories.join(','));
                }
                if (pendingSeries.length > 0) {
                    params.set('series', pendingSeries.join(','));
                }
                const res = await authFetch(`${API_BASE}/api/categories?${params}`);
                const newCategories = await res.json();
                setCategories(newCategories);
                // Clear any selected categories that are no longer available
                if (pendingSubcategories.length > 0 || pendingSeries.length > 0) {
                    setPendingCategories(prev => {
                        const filtered = prev.filter(c => newCategories.includes(c));
                        return filtered.length === prev.length ? prev : filtered;
                    });
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }

        fetchCategories();
    }, [pendingSubcategories, pendingSeries]);

    // Fetch subcategories (filtered by selected categories and series - bidirectional cascading)
    useEffect(() => {
        async function fetchSubcategories() {
            try {
                const params = new URLSearchParams();
                if (pendingCategories.length > 0) {
                    params.set('categories', pendingCategories.join(','));
                }
                if (pendingSeries.length > 0) {
                    params.set('series', pendingSeries.join(','));
                }
                const res = await authFetch(`${API_BASE}/api/subcategories?${params}`);
                const newSubcategories = await res.json();
                setSubcategories(newSubcategories);
                // Clear any selected subcategories that are no longer available
                if (pendingCategories.length > 0 || pendingSeries.length > 0) {
                    setPendingSubcategories(prev => {
                        const filtered = prev.filter(s => newSubcategories.includes(s));
                        return filtered.length === prev.length ? prev : filtered;
                    });
                }
            } catch (error) {
                console.error('Error fetching subcategories:', error);
            }
        }

        fetchSubcategories();
    }, [pendingCategories, pendingSeries]);

    // Fetch series (filtered by selected categories and subcategories)
    useEffect(() => {
        async function fetchSeries() {
            try {
                const params = new URLSearchParams();
                if (pendingCategories.length > 0) {
                    params.set('categories', pendingCategories.join(','));
                }
                if (pendingSubcategories.length > 0) {
                    params.set('subcategories', pendingSubcategories.join(','));
                }
                const res = await authFetch(`${API_BASE}/api/series?${params}`);
                const newSeries = await res.json();
                setSeries(newSeries);
                // Clear any selected series that are no longer available
                if (pendingCategories.length > 0 || pendingSubcategories.length > 0) {
                    setPendingSeries(prev => {
                        const filtered = prev.filter(s => newSeries.includes(s));
                        return filtered.length === prev.length ? prev : filtered;
                    });
                }
            } catch (error) {
                console.error('Error fetching series:', error);
            }
        }

        fetchSeries();
    }, [pendingCategories, pendingSubcategories]);

    // Fetch items based on filters and search
    useEffect(() => {
        async function fetchItems() {
            try {
                const params = new URLSearchParams();
                if (pendingCategories.length > 0) {
                    params.set('categories', pendingCategories.join(','));
                }
                if (pendingSubcategories.length > 0) {
                    params.set('subcategories', pendingSubcategories.join(','));
                }
                if (pendingSeries.length > 0) {
                    params.set('series', pendingSeries.join(','));
                }
                if (searchTerm) {
                    params.set('search', searchTerm);
                }
                if (pendingSku) {
                    params.set('sku', pendingSku);
                }

                const res = await authFetch(`${API_BASE}/api/items?${params}`);
                setItems(await res.json());
            } catch (error) {
                console.error('Error fetching items:', error);
            }
        }

        fetchItems();
    }, [pendingCategories, pendingSubcategories, pendingSeries, searchTerm, pendingSku]);

    // Fetch promotions when applied filters change
    useEffect(() => {
        async function fetchPromotions() {
            try {
                const params = new URLSearchParams();
                if (appliedFilters.categories.length > 0) {
                    params.set('categories', appliedFilters.categories.join(','));
                }
                if (appliedFilters.subcategories.length > 0) {
                    params.set('subcategories', appliedFilters.subcategories.join(','));
                }
                if (appliedFilters.series.length > 0) {
                    params.set('series', appliedFilters.series.join(','));
                }
                if (appliedFilters.items.length > 0) {
                    params.set('items', appliedFilters.items.join(','));
                }
                if (appliedFilters.promoTypes.length > 0) {
                    params.set('promoTypes', appliedFilters.promoTypes.join(','));
                }
                if (appliedFilters.months.length > 0) {
                    // Convert month abbreviations to numbers (1-12)
                    const monthNums = appliedFilters.months.map(m =>
                        monthOptions.indexOf(m) + 1
                    );
                    params.set('months', monthNums.join(','));
                }
                if (appliedFilters.promoName) {
                    params.set('promoName', appliedFilters.promoName);
                }
                if (appliedFilters.sku) {
                    params.set('sku', appliedFilters.sku);
                }

                const res = await authFetch(`${API_BASE}/api/promotions?${params}`);
                const promoData = await res.json();
                initializePromoColors(promoData);
                setPromotions(promoData);
            } catch (error) {
                console.error('Error fetching promotions:', error);
            }
        }

        fetchPromotions();
    }, [appliedFilters]);

    // Fetch all promotions (without item/promoType filters) for "View full calendar" mode
    useEffect(() => {
        if (!viewFullCalendar) {
            setAllPromotions(null);
            return;
        }

        async function fetchAllPromotions() {
            try {
                // Fetch promotions with category/subcategory/series/month filters only
                // (exclude item and promoType filters so we get the full calendar)
                const params = new URLSearchParams();
                if (appliedFilters.categories.length > 0) {
                    params.set('categories', appliedFilters.categories.join(','));
                }
                if (appliedFilters.subcategories.length > 0) {
                    params.set('subcategories', appliedFilters.subcategories.join(','));
                }
                if (appliedFilters.series.length > 0) {
                    params.set('series', appliedFilters.series.join(','));
                }
                if (appliedFilters.months.length > 0) {
                    const monthNums = appliedFilters.months.map(m =>
                        monthOptions.indexOf(m) + 1
                    );
                    params.set('months', monthNums.join(','));
                }
                if (appliedFilters.promoName) {
                    params.set('promoName', appliedFilters.promoName);
                }

                const res = await authFetch(`${API_BASE}/api/promotions?${params}`);
                const promoData = await res.json();
                initializePromoColors(promoData);
                setAllPromotions(promoData);
            } catch (error) {
                console.error('Error fetching all promotions:', error);
            }
        }

        fetchAllPromotions();
    }, [viewFullCalendar, appliedFilters]);

    // Apply filters
    const handleApplyFilters = useCallback(() => {
        setAppliedFilters({
            categories: [...pendingCategories],
            subcategories: [...pendingSubcategories],
            series: [...pendingSeries],
            items: [...pendingItems],
            promoTypes: [...pendingPromoTypes],
            months: [...pendingMonths],
            promoName: promoNameSearch,
            sku: pendingSku
        });
    }, [pendingCategories, pendingSubcategories, pendingSeries, pendingItems, pendingPromoTypes, pendingMonths, promoNameSearch, pendingSku]);

    // Clear all filters
    const handleClearAll = useCallback(() => {
        setPendingCategories([]);
        setPendingSubcategories([]);
        setPendingSeries([]);
        setPendingItems([]);
        setPendingPromoTypes([]);
        setPendingMonths([]);
        setSearchTerm('');
        setPromoNameSearch('');
        setPendingSku('');
        setAppliedFilters({
            categories: [],
            subcategories: [],
            series: [],
            items: [],
            promoTypes: [],
            months: [],
            promoName: '',
            sku: ''
        });
    }, []);

    // Check if there are pending changes
    const hasPendingChanges = useMemo(() => {
        return (
            JSON.stringify(pendingCategories) !== JSON.stringify(appliedFilters.categories) ||
            JSON.stringify(pendingSubcategories) !== JSON.stringify(appliedFilters.subcategories) ||
            JSON.stringify(pendingSeries) !== JSON.stringify(appliedFilters.series) ||
            JSON.stringify(pendingItems) !== JSON.stringify(appliedFilters.items) ||
            JSON.stringify(pendingPromoTypes) !== JSON.stringify(appliedFilters.promoTypes) ||
            JSON.stringify(pendingMonths) !== JSON.stringify(appliedFilters.months) ||
            promoNameSearch !== appliedFilters.promoName ||
            pendingSku !== appliedFilters.sku
        );
    }, [pendingCategories, pendingSubcategories, pendingSeries, pendingItems, pendingPromoTypes, pendingMonths, promoNameSearch, pendingSku, appliedFilters]);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return (
            appliedFilters.categories.length > 0 ||
            appliedFilters.subcategories.length > 0 ||
            appliedFilters.series.length > 0 ||
            appliedFilters.items.length > 0 ||
            appliedFilters.promoTypes.length > 0 ||
            appliedFilters.months.length > 0 ||
            appliedFilters.promoName.length > 0 ||
            appliedFilters.sku.length > 0
        );
    }, [appliedFilters]);

    // Compute effective filter items - use explicitly selected items if any,
    // otherwise use all items that match category/subcategory/series filters
    const effectiveFilterItems = useMemo(() => {
        if (appliedFilters.items.length > 0) {
            // User explicitly selected items, use those
            return appliedFilters.items;
        }
        // If category/subcategory/series filters are applied, use the filtered items list
        if (appliedFilters.categories.length > 0 ||
            appliedFilters.subcategories.length > 0 ||
            appliedFilters.series.length > 0) {
            return items;
        }
        // No relevant filters applied
        return [];
    }, [appliedFilters, items]);

    // Build a set of filtered promo keys for dimming in "View full calendar" mode
    const filteredPromoKeys = useMemo(() => {
        const keys = new Set();
        promotions.forEach(p => {
            keys.add(`${p.name}|${p.startDate}|${p.endDate}`);
        });
        return keys;
    }, [promotions]);

    // Use all promotions when "View full calendar" is on, otherwise use filtered
    const displayPromotions = useMemo(() => {
        if (viewFullCalendar && allPromotions) {
            return allPromotions;
        }
        return promotions;
    }, [viewFullCalendar, allPromotions, promotions]);

    // Group promotions by year and week
    const groupedPromos = useMemo(() => {
        return groupPromosByYearAndWeek(displayPromotions, years);
    }, [displayPromotions, years]);

    // Build table data with up to 53 weeks (ISO years can have 52 or 53 weeks)
    const tableData = useMemo(() => {
        const allWeeks = [];
        // Check if any year has week 53 with promotions
        const maxWeek = Math.max(52, ...years.map(year => {
            const weeks = Object.keys(groupedPromos[year] || {}).map(Number);
            return weeks.length > 0 ? Math.max(...weeks) : 52;
        }));

        for (let w = 1; w <= maxWeek; w++) {
            const weekData = {};

            years.forEach(year => {
                const weekPromos = groupedPromos[year]?.[w] || [];
                weekData[year] = assignPromoColors(weekPromos);
            });

            allWeeks.push({ week: w, years: weekData });
        }

        return allWeeks;
    }, [groupedPromos, years]);

    // Generate stats message
    const statsMessage = useMemo(() => {
        if (appliedFilters.items.length > 0) {
            return `Showing promotions for: ${appliedFilters.items.join(', ')}`;
        }

        const filterParts = [];
        if (appliedFilters.categories.length > 0) {
            filterParts.push(`Categories: ${appliedFilters.categories.join(', ')}`);
        }
        if (appliedFilters.subcategories.length > 0) {
            filterParts.push(`Subcategories: ${appliedFilters.subcategories.join(', ')}`);
        }
        if (appliedFilters.series.length > 0) {
            filterParts.push(`Series: ${appliedFilters.series.join(', ')}`);
        }
        if (appliedFilters.promoTypes.length > 0) {
            filterParts.push(`Promo Types: ${appliedFilters.promoTypes.join(', ')}`);
        }
        if (appliedFilters.months.length > 0) {
            filterParts.push(`Months: ${appliedFilters.months.join(', ')}`);
        }
        if (appliedFilters.promoName) {
            filterParts.push(`Promo Name: "${appliedFilters.promoName}"`);
        }

        if (filterParts.length > 0) {
            return `Filtered by: ${filterParts.join(' | ')}`;
        }

        return 'Showing all promotions. Use filters and click "Apply Filters" to narrow down results.';
    }, [appliedFilters]);

    // Show auth loading state
    if (authLoading) {
        return <div className="loading">Authenticating...</div>;
    }

    // Show auth error (user not authorized)
    if (authError) {
        const handleRetry = () => {
            instance.loginRedirect(loginRequest);
        };

        const handleLogout = () => {
            instance.logoutRedirect({ postLogoutRedirectUri: '/' });
        };

        return (
            <div className="auth-error">
                <h2>Access Denied</h2>
                <p>User not authorized. Please contact an administrator.</p>
                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button onClick={handleRetry} style={{ padding: '10px 20px', cursor: 'pointer' }}>
                        Try Again
                    </button>
                    <button onClick={handleLogout} style={{ padding: '10px 20px', cursor: 'pointer', background: 'var(--text-secondary)' }}>
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="loading">Loading promotion data...</div>;
    }

    return (
        <div className="container">
            <div className="sticky-header">
                {/* Header Bar */}
                <div className="header-bar">
                    <div className="header-left">
                        <h1>Promotion Calendar <span className="us-only">(U.S. only)</span></h1>
                    </div>
                    <div className="header-right">
                        <div className="user-info">
                            <span className="user-icon">👤</span>
                            <span className="user-name">{currentUser?.displayName || currentUser?.email}</span>
                        </div>
                        {isAdmin && (
                            <button className="header-btn admin-btn" onClick={() => setShowAdminPanel(true)}>
                                Admin
                            </button>
                        )}
                        <button
                            className="theme-toggle"
                            onClick={cycleTheme}
                            title={`Theme: ${theme}`}
                        >
                            {themeIcon}
                        </button>
                        <span className="version">v2.4.0</span>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="filter-section">
                    {/* Row 1: Item Filters */}
                    <div className="filter-row">
                        <MultiSelect
                            label="Category"
                            options={categories}
                            selected={pendingCategories}
                            onChange={setPendingCategories}
                            placeholder="Category"
                        />
                        <MultiSelect
                            label="Subcategory"
                            options={subcategories}
                            selected={pendingSubcategories}
                            onChange={setPendingSubcategories}
                            placeholder="Subcategory"
                        />
                        <MultiSelect
                            label="Series"
                            options={series}
                            selected={pendingSeries}
                            onChange={setPendingSeries}
                            placeholder="Series"
                        />
                        <MultiSelect
                            label="Items"
                            options={items}
                            selected={pendingItems}
                            onChange={setPendingItems}
                            placeholder="Items"
                            showSelectAll={true}
                            wide={true}
                        />
                        <div className="search-wrapper">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search Item Name"
                                className="search-input"
                            />
                        </div>
                        <div className="search-wrapper">
                            <input
                                type="text"
                                value={pendingSku}
                                onChange={(e) => setPendingSku(e.target.value)}
                                placeholder="Search SKU"
                                className="search-input"
                            />
                        </div>
                    </div>

                    {/* Row 2: Promo Filters */}
                    <div className="filter-row filter-row-2">
                        <MultiSelect
                            label="Promo Type"
                            options={promoTypes}
                            selected={pendingPromoTypes}
                            onChange={setPendingPromoTypes}
                            placeholder="Type"
                        />
                        <MultiSelect
                            label="Promo Month"
                            options={monthOptions}
                            selected={pendingMonths}
                            onChange={setPendingMonths}
                            placeholder="Month"
                        />
                        <div className="search-wrapper">
                            <input
                                type="text"
                                value={promoNameSearch}
                                onChange={(e) => setPromoNameSearch(e.target.value)}
                                placeholder="Search Promo Name"
                                className="search-input"
                            />
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="action-bar">
                        {hasActiveFilters && (
                            <div className="action-left">
                                <div className="toggle-container">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={viewFilteredOnly}
                                            onChange={(e) => setViewFilteredOnly(e.target.checked)}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">View only filtered item(s)</span>
                                </div>
                                <div className="toggle-container">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={viewFullCalendar}
                                            onChange={(e) => setViewFullCalendar(e.target.checked)}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">View full calendar</span>
                                </div>
                            </div>
                        )}
                        <div className="action-right">
                            <button
                                className={`apply-btn ${hasPendingChanges ? 'has-changes' : ''}`}
                                onClick={handleApplyFilters}
                                disabled={!hasPendingChanges}
                            >
                                Apply Filters
                            </button>
                            {(hasActiveFilters || searchTerm || promoNameSearch || pendingSku) && (
                                <button className="secondary" onClick={handleClearAll}>
                                    Clear All
                                </button>
                            )}
                            <button
                                className="export-btn"
                                onClick={async () => {
                                    try {
                                        const res = await authFetch(`${API_BASE}/api/export/promotions`);
                                        const blob = await res.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'Promo Details Export.csv';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    } catch (err) {
                                        console.error('Export failed:', err);
                                    }
                                }}
                                title="Export all promotions to CSV"
                            >
                                Export CSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* Active filters display */}
                {hasActiveFilters && (
                    <div className="active-filters">
                        <span className="filter-badge">Active Filters: <span className="filter-badge-hint">(hover for details)</span></span>
                        {appliedFilters.items.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.items.join(', ')}>Items: {appliedFilters.items.length} selected</span>
                        )}
                        {appliedFilters.categories.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.categories.join(', ')}>Categories: {appliedFilters.categories.length}</span>
                        )}
                        {appliedFilters.subcategories.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.subcategories.join(', ')}>Subcategories: {appliedFilters.subcategories.length}</span>
                        )}
                        {appliedFilters.series.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.series.join(', ')}>Series: {appliedFilters.series.length}</span>
                        )}
                        {appliedFilters.promoTypes.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.promoTypes.join(', ')}>Types: {appliedFilters.promoTypes.length}</span>
                        )}
                        {appliedFilters.months.length > 0 && (
                            <span className="filter-chip" title={appliedFilters.months.join(', ')}>Months: {appliedFilters.months.length}</span>
                        )}
                        {appliedFilters.promoName && (
                            <span className="filter-chip" title={appliedFilters.promoName}>Promo: "{appliedFilters.promoName}"</span>
                        )}
                        {appliedFilters.sku && (
                            <span className="filter-chip" title={appliedFilters.sku}>SKU: "{appliedFilters.sku}"</span>
                        )}
                    </div>
                )}
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th className="week-col">Week</th>
                            {years.map(year => (
                                <th key={year} className="year-col">
                                    {year}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map(({ week, years: yearData }) => (
                            <tr key={week}>
                                <td className="week-cell">{week}</td>
                                {years.map((year, yearIdx) => {
                                    const promos = yearData[year];
                                    const isYearSeparator = yearIdx > 0;

                                    // For week 53, check if this year actually has a week 53
                                    const hasThisWeek = week < 53 || yearHasWeek53(year);
                                    const weekRange = hasThisWeek ? formatWeekRange(year, week) : '';

                                    if (!promos || promos.length === 0) {
                                        return (
                                            <td
                                                key={year}
                                                className={isYearSeparator ? "empty year-separator" : "empty"}
                                                title={weekRange}
                                            >
                                                {hasThisWeek && (
                                                    <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginBottom: '2px' }}>
                                                        {weekRange}
                                                    </div>
                                                )}
                                                {hasThisWeek ? '—' : ''}
                                            </td>
                                        );
                                    }

                                    // Group promos by name within this week
                                    const groupedByName = groupPromosByName(promos);

                                    return (
                                        <td
                                            key={year}
                                            className={isYearSeparator ? "year-separator" : ""}
                                        >
                                            <div className="cell-content">
                                                {hasThisWeek && (
                                                    <div className="week-date-header">
                                                        {weekRange}
                                                    </div>
                                                )}
                                                <div className="promos-wrapper">
                                                    {groupedByName.map((groupedPromo, idx) => (
                                                        groupedPromo.instances.length === 1 ? (
                                                            <PromoBox
                                                                key={idx}
                                                                promo={groupedPromo.instances[0]}
                                                                selectedItems={effectiveFilterItems}
                                                                onShowDetails={setModalPromo}
                                                                bgColor={groupedPromo.bgColor}
                                                                borderColor={groupedPromo.color}
                                                                showFilteredOnly={viewFilteredOnly}
                                                                viewFullCalendar={viewFullCalendar}
                                                                filteredPromoKeys={filteredPromoKeys}
                                                            />
                                                        ) : (
                                                            <GroupedPromoBox
                                                                key={idx}
                                                                groupedPromo={groupedPromo}
                                                                selectedItems={effectiveFilterItems}
                                                                onShowDetails={setModalPromo}
                                                                bgColor={groupedPromo.bgColor}
                                                                borderColor={groupedPromo.color}
                                                                showFilteredOnly={viewFilteredOnly}
                                                                viewFullCalendar={viewFullCalendar}
                                                                filteredPromoKeys={filteredPromoKeys}
                                                            />
                                                        )
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal for promotion details */}
            {modalPromo && (
                <PromoModal
                    promo={modalPromo}
                    onClose={() => setModalPromo(null)}
                    showFilteredOnly={viewFilteredOnly}
                    filteredItems={
                        effectiveFilterItems.length > 0
                            ? modalPromo.items.filter(item => effectiveFilterItems.includes(item.groupedName || item.item))
                            : null
                    }
                />
            )}

            {/* Admin panel */}
            {showAdminPanel && isAdmin && (
                <AdminPanel
                    currentUser={currentUser}
                    isSuperAdmin={isSuperAdmin}
                    onClose={() => {
                        setShowAdminPanel(false);
                        handleApplyFilters();
                    }}
                />
            )}

            {/* Feedback button + modal */}
            <FeedbackButton onClick={() => setShowFeedback(true)} />
            {showFeedback && (
                <FeedbackModal onClose={() => setShowFeedback(false)} />
            )}
        </div>
    );
}

function App() {
    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={loginRequest}
            loadingComponent={() => <div className="loading">Signing you in…</div>}
        >
            <AppContent />
        </MsalAuthenticationTemplate>
    );
}

export default App;
